// Small presentation helpers shared by every artist list.

const PALETTE = ['#2E4A7A', '#8A3A55', '#55632B', '#7A5A1E', '#3F5E4A', '#7A3B2E', '#4F5F6B', '#9A4321'];

/** Name for A–Z sorting: a leading "The " is ignored ("The Velvet Underground" files under V). */
export function sortName(name: string): string {
  return name.replace(/^the\s+/i, '');
}

export function initials(name: string): string {
  const words = sortName(name).split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? '')
    .join('')
    .toUpperCase();
}

export function colorFor(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.codePointAt(0)!) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

export type LetterGroup<T> = { letter: string; items: T[] };

/** Groups by first letter of sortName; digits and symbols go under "#", after Z. */
export function groupAZ<T extends { name: string }>(items: T[]): LetterGroup<T>[] {
  const sorted = [...items].sort((a, b) => collator.compare(sortName(a.name), sortName(b.name)));
  const groups: LetterGroup<T>[] = [];
  for (const item of sorted) {
    const first = sortName(item.name)
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .charAt(0)
      .toUpperCase();
    const letter = /[A-Z]/.test(first) ? first : '#';
    let g = groups.find((x) => x.letter === letter);
    if (!g) groups.push((g = { letter, items: [] }));
    g.items.push(item);
  }
  return groups.sort((a, b) => (a.letter === '#' ? 1 : b.letter === '#' ? -1 : a.letter.localeCompare(b.letter)));
}

/** Splits `text` around the first case-insensitive match of `q`, for highlighting. */
export function splitMatch(text: string, q: string): [string, string, string] {
  const i = q ? text.toLowerCase().indexOf(q.trim().toLowerCase()) : -1;
  if (i < 0 || !q.trim()) return [text, '', ''];
  const n = q.trim().length;
  return [text.slice(0, i), text.slice(i, i + n), text.slice(i + n)];
}

export function genreMeta(genres: string[]): string {
  if (!genres.length) return 'Artist';
  return genres
    .slice(0, 2)
    .map((g) => g.charAt(0).toUpperCase() + g.slice(1))
    .join(' · ');
}
